/**
 * THE THREE THINGS THEY CHECK FIRST (ops #86, `docs/plan-coach-review.md` section 4): three boxes, one
 * thing each, joined by newlines into the one text the home keeps (`sites.checks_first`). Drawn on the
 * booking form and on the home's page, so both ask the same question the same way.
 *
 * The server holds the rule (three lines, 400 characters, the claims check, no code) and trims each
 * line; this draws the boxes, says what the server found under them, and counts the characters so the
 * office sees the limit before the server says it.
 */
import type { ClaimsFinding } from '@fk/shared';
import { CHECKS_FIRST_MAX_CHARACTERS, CHECKS_FIRST_MAX_LINES } from '@fk/shared';
import { TextField } from './Field.tsx';
import { WordsCheck } from './WordsCheck.tsx';

/** The question the office asks, word for word, under the heading. */
export const CHECKS_FIRST_ASK =
  'Ask them: when you walk in after a clean, what are the three things you look at first? One thing a box.';

/** Above what the claims check found in the boxes: the cleaner reads them on the sheet. */
export const CHECKS_FIRST_REFUSED =
  'Not saved. The cleaner reads these on the sheet, so change the words marked here and save it again.';

/** The boxes as one text, the way the server keeps it, or null when every box is empty. */
export function joinChecksFirst(lines: readonly string[]): string | null {
  const kept = lines.map((line) => line.trim()).filter((line) => line !== '');
  return kept.length === 0 ? null : kept.join('\n');
}

export function ChecksFirstBoxes({
  idPrefix,
  lines,
  onChange,
  refused,
}: {
  /** Unique on the page: `checks-first` gives `checks-first-1` to `checks-first-3`. */
  idPrefix: string;
  /** Exactly `CHECKS_FIRST_MAX_LINES` strings, one a box. */
  lines: readonly string[];
  onChange: (lines: string[]) => void;
  /** What the server found in the boxes when it refused them, until the words change. */
  refused?: readonly ClaimsFinding[];
}) {
  const joined = joinChecksFirst(lines) ?? '';
  const over = joined.length > CHECKS_FIRST_MAX_CHARACTERS;
  return (
    <fieldset className="mt-1">
      <legend className="text-sm font-medium">They check first</legend>
      <p className="mt-1 mb-2 text-xs text-navy/65">{CHECKS_FIRST_ASK}</p>
      {Array.from({ length: CHECKS_FIRST_MAX_LINES }, (_, index) => (
        <TextField
          key={index}
          id={`${idPrefix}-${index + 1}`}
          label={`Thing ${index + 1}`}
          value={lines[index] ?? ''}
          onChange={(value) => {
            const next = Array.from(
              { length: CHECKS_FIRST_MAX_LINES },
              (__, at) => lines[at] ?? '',
            );
            next[index] = value.replace(/\r?\n/g, ' ');
            onChange(next);
          }}
        />
      ))}
      {over ? (
        <p className="mb-2 text-xs text-coral">
          {joined.length} characters. The three together are at most {CHECKS_FIRST_MAX_CHARACTERS}.
        </p>
      ) : null}
      <WordsCheck words={joined} refused={refused} refusedTitle={CHECKS_FIRST_REFUSED} />
    </fieldset>
  );
}
